import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import * as Icon from "react-feather";
import Moment from "react-moment";
import NoData from "components/no-data/NoData";
import TableComponent from "../../components/widgets/table/index";
import { fetchEntitySystemQuotations } from "services/apiSrv";

export default function Quotations({
  add_asset,
  search,
  searchSpace,
}) {
  const history = useHistory();
  const [quotationData, setQuotationData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    console.log("[Quotations] Component mounted. Starting API fetch.");
    console.log("[Quotations] entityRef from sessionStorage:", sessionStorage.getItem("entityRef"));
    fetchEntitySystemQuotations()
      .then(res => {
        console.log("[Quotations] API success - Raw response:", JSON.stringify(res, null, 2));
        console.log("[Quotations] Checking res.data:", JSON.stringify(res.data, null, 2));
        const dataArray = res.data || [];
        console.log("[Quotations] Extracted dataArray:", JSON.stringify(dataArray, null, 2));
        const mappedData = dataArray.map(item => {
          const mappedItem = {
            guest_client_quotation_ref: item.systemQuotationRef || "",
            client_names: item.clientDetails?.names ? 
              `${item.clientDetails.names.fname} ${item.clientDetails.names.lname}`.trim() || "Unknown Client" : 
              "Unknown Client",
            created_date: item.createdAt || new Date().toISOString(),
            status: item.quotationStatus?.toUpperCase() || "PENDING:IN-PROGRESS",
            category: item.insuranceClassRef || "General"
          };
          console.log("[Quotations] Mapped item:", JSON.stringify(mappedItem, null, 2));
          return mappedItem;
        }).filter(item => {
          if (!item.guest_client_quotation_ref) {
            console.warn("[Quotations] Filtering out item with missing guest_client_quotation_ref:", JSON.stringify(item, null, 2));
            return false;
          }
          return true;
        });
        console.log("[Quotations] Mapped and filtered data:", JSON.stringify(mappedData, null, 2));
        setQuotationData(mappedData);
        setLoading(false);
        console.log("[Quotations] State updated: loading=false, quotationData set with length:", mappedData.length);
      })
      .catch(err => {
        console.error("[Quotations] API error - Details:", err);
        console.error("[Quotations] API error stack:", err.stack);
        setError("Failed to load quotations. Please try again.");
        setLoading(false);
        console.log("[Quotations] State updated on error: loading=false, error set.");
      });
  }, []);

  React.useEffect(() => {
    console.log("[Quotations] quotationData state changed:", JSON.stringify(quotationData, null, 2));
  }, [quotationData]);

  const quotationStats = useMemo(() => {
    console.log("[Quotations] Computing quotationStats - Input quotationData:", JSON.stringify(quotationData, null, 2));
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const todayCount = quotationData.filter(q => {
      const isValid = new Date(q.created_date) >= startOfToday;
      console.log("[Quotations] todayCount filter - created_date:", q.created_date, "isValid:", isValid);
      return isValid;
    }).length;

    const weekCount = quotationData.filter(q => {
      const isValid = new Date(q.created_date) >= startOfWeek;
      console.log("[Quotations] weekCount filter - created_date:", q.created_date, "isValid:", isValid);
      return isValid;
    }).length;

    const monthCount = quotationData.filter(q => {
      const isValid = new Date(q.created_date) >= startOfMonth;
      console.log("[Quotations] monthCount filter - created_date:", q.created_date, "isValid:", isValid);
      return isValid;
    }).length;

    const stats = { todayCount, weekCount, monthCount };
    console.log("[Quotations] Computed quotationStats:", JSON.stringify(stats, null, 2));
    return stats;
  }, [quotationData]);

  const handleViewClick = (id) => {
    console.log("[Quotations] handleViewClick triggered with systemQuotationRef:", id);
    const targetUrl = `/dashboard/quotations/${id}`;
    console.log("[Quotations] Navigating to URL:", targetUrl);
    console.log("[Quotations] Verifying systemQuotationRef before navigation:", id ? id : "No systemQuotationRef provided");
    history.push(targetUrl);
    console.log("[Quotations] Navigation executed for systemQuotationRef:", id);
  };

  const filteredQuotations = quotationData.filter((data) => {
    if (!data || !data.guest_client_quotation_ref) {
      console.warn("[Quotations] Skipping invalid quotation data in filter:", JSON.stringify(data, null, 2));
      return false;
    }
    if (search === '') return true;
    return (
      data?.client_names?.toLowerCase()?.includes(search.toLowerCase()) ||
      data?.created_date?.toLowerCase()?.includes(search.toLowerCase()) ||
      data?.guest_client_quotation_ref?.toLowerCase()?.includes(search.toLowerCase()) ||
      data?.status?.toLowerCase()?.includes(search.toLowerCase())
    );
  });
  console.log("[Quotations] Computed filteredQuotations (search='" + search + "'):", JSON.stringify(filteredQuotations, null, 2));

  const tableFields = [
    { name: "Ref" },
    { name: "Customer" },
    { name: "Status" },
    { name: "Date" },
    { name: "Category" },
    { name: "Actions" },
  ];

  const rowData = [
    {
      type: "text",
      field: "guest_client_quotation_ref",
    },
    {
      type: "nested",
      field: "client_names",
      rows: [
        {
          field: "client_names",
          class: "font-weight-medium text-dark",
        },
        {
          field: "client_names",
          class: "text-secondary small",
          render: () => "Client",
        },
      ],
      render: (data) => (
        <div className="d-flex align-items-center">
          <div className="mr-3">
            <div
              className="bg-primary bg-opacity-50 rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: "40px", height: "40px" }}
            >
              <span
                className="text-white fw-bold"
                style={{ textShadow: "1px 1px 2px rgba(0,0,0,0.3)" }}
              >
                {data?.client_names?.charAt(0)?.toUpperCase() || "C"}
              </span>
            </div>
          </div>
          <div>
            <div className="font-weight-medium text-dark">{data?.client_names}</div>
            <small className="text-secondary">Client</small>
          </div>
        </div>
      ),
    },
    {
      type: "status",
      field: "status",
      render: (data) => (
        <span
          className={`badge ${
            data.status === "PENDING:IN-PROGRESS"
              ? "bg-info text-white"
              : data.status === "PENDING:PURCHASE"
              ? "bg-warning text-dark"
              : data.status === "CLOSED:REJECTED"
              ? "bg-danger text-white"
              : "bg-success text-white"
          } rounded-pill px-3 py-1`}
        >
          {data.status}
        </span>
      ),
    },
    {
      type: "date",
      field: "created_date",
      render: (data) => (
        <span className="text-secondary">
          <Moment format="DD MMM YYYY" date={data.created_date} />
        </span>
      ),
    },
    {
      type: "text",
      field: "category",
      render: (data) => (
        <span className="badge bg-primary text-white rounded-pill px-3 py-1">
          {data?.category || "General"}
        </span>
      ),
    },
    {
      type: "action",
      actions: [
        {
          method: (quotationRef) => {
            console.log("[Quotations] View action clicked for guest_client_quotation_ref:", quotationRef);
            if (!quotationRef) {
              console.error("[Quotations] Invalid guest_client_quotation_ref received:", quotationRef);
              return;
            }
            handleViewClick(quotationRef);
          },
          field: "guest_client_quotation_ref",
          title: "View",
        },
      ],
    },
  ];

  console.log("[Quotations] Rendering - TableComponent props - table_data:", JSON.stringify(filteredQuotations, null, 2));
  console.log("[Quotations] Rendering - loading:", loading, "error:", error, "filteredQuotations.length:", filteredQuotations.length);

  return (
    <div className="container my-5">
      <div className="mb-5">
        <h3 className="fw-bold text-dark mb-2">My Quotations</h3>
        <p className="text-secondary mb-0">Track and manage your quotations</p>
      </div>

      <div className="row g-4 mb-5">
        <div className="col-md-4">
          <div className="card shadow-sm h-100 border-0">
            <div className="card-body text-center py-4">
              <div
                className="bg-success bg-opacity-50 rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{ width: "60px", height: "60px" }}
              >
                <span
                  className="text-white fw-bold fs-4"
                  style={{ textShadow: "1px 1px 2px rgba(0,0,0,0.3)" }}
                >
                  T
                </span>
              </div>
              <h6 className="card-title text-secondary mb-2">Today's Quotations</h6>
              <h3 className="text-success fw-bold mb-0">{quotationStats.todayCount}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm h-100 border-0">
            <div className="card-body text-center py-4">
              <div
                className="bg-primary bg-opacity-50 rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{ width: "60px", height: "60px" }}
              >
                <span
                  className="text-white fw-bold fs-4"
                  style={{ textShadow: "1px 1px 2px rgba(0,0,0,0.3)" }}
                >
                  W
                </span>
              </div>
              <h6 className="card-title text-secondary mb-2">Weekly Quotations</h6>
              <h3 className="text-primary fw-bold mb-0">{quotationStats.weekCount}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm h-100 border-0">
            <div className="card-body text-center py-4">
              <div
                className="bg-info bg-opacity-50 rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{ width: "60px", height: "60px" }}
              >
                <span
                  className="text-white fw-bold fs-4"
                  style={{ textShadow: "1px 1px 2px rgba(0,0,0,0.3)" }}
                >
                  M
                </span>
              </div>
              <h6 className="card-title text-secondary mb-2">Monthly Quotations</h6>
              <h3 className="text-info fw-bold mb-0">{quotationStats.monthCount}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h5 className="mb-1">Quotation History</h5>
              <p className="text-secondary mb-0 small">
                View and manage all your quotation records
              </p>
            </div>
            <div className="ms-3" style={{ maxWidth: "300px" }}>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <Icon.Search size={16} className="text-secondary" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search quotations..."
                  onChange={(e) => searchSpace(e)}
                />
              </div>
            </div>
          </div>

          {loading ? (
            <p>Loading quotations...</p>
          ) : error ? (
            <p>{error}</p>
          ) : filteredQuotations.length === 0 ? (
            <NoData message="You currently don't have any quotations" />
          ) : (
            <TableComponent
              title=""
              table_fields={tableFields}
              table_data={filteredQuotations}
              row_data={rowData}
              pageSize={10}
              table_class="table-hover"
              handlePaginationChange={() => {}}
            />
          )}
        </div>
      </div>
    </div>
  );
}